import React, { Fragment, useEffect, useState } from "react";
import { SolidButton } from "../../global/SolidButton";
import { CirclePlus, EyeIcon, EyeOff, Trash } from "lucide-react";
import MyCard from "../../tools/MyCard";
import Pagination from "../../util/Pagination";
import { MyModal } from "../../global/MyModal";
import BigToggle from "../../util/BigToggle";
import { BorderButton } from "../../global/BorderButton";
import {
  addSystemConfig,
  deleteSystemConfig,
  getSystemConfigs,
  updateSystemConfig,
} from "../apps_manage/api";
import { PencilSimpleLine } from "phosphor-react";
import toast from "react-hot-toast";

export default function SystemSettings() {
  const [activeTab, setActiveTab] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [dataType, selectDataType] = useState("string");
  const [isPublic, setIsPublic] = useState(true);
  const [name, setName] = useState("");
  const [value, setValue] = useState("");
  const [description, setDescription] = useState("");
  const [errors, setErrors] = useState({});
  const [systemConfigs, setSystemConfigs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [search, setSearch] = useState("");
  const [isDisabled, setDisabled] = useState(false);
  const [currentConfigId, setCurrentConfigId] = useState(null);

  //pagination
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(30);
  const [total, setTotal] = useState(0);

  const fetchConfigData = async () => {
    try {
      const payload = {
        params: {
          page: page,
          limit: limit,
          search: search,
          //  status: activeTab === 1 ? "public" : "private",
        },
      };
      const res = await getSystemConfigs(payload);
      if (res?.status === 200) {
        setSystemConfigs(res?.data?.configs);
      }
    } catch (err) {
      console.error("failed to fetch config data :", err);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchConfigData();
  }, [page, limit, search]);

  const validateForm = () => {
    const errs = {};
    if (!name || !name.trim()) {
      errs.name = "Name is required";
    } else if (!/^[A-Z0-9_]+$/.test(name.trim())) {
      errs.name = "Uppercase letters, numbers and underscores only";
    }

    if (!value || !value.toString().trim()) {
      errs.value = "Value is required";
    } else if (dataType === "number" && isNaN(Number(value))) {
      errs.value = "Value must be a valid number";
    } else if (
      dataType === "boolean" &&
      !/^(true|false)$/i.test(value.toString().trim())
    ) {
      errs.value = "Enter 'true' or 'false' for boolean type";
    }

    if (!description || !description.trim()) {
      errs.description = "Description is required";
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // update
  const openEditModal = (config) => {
    setIsEditMode(true);
    setIsModalOpen(true);
    setCurrentConfigId(config.id);
    setName(config.name);
    setValue(config.value);
    setDescription(config.comment);
  };
  // ------------------------------
  // DELETE CONFIG
  // ------------------------------
  const handleDelete = async () => {
    //setAppConfigs(appConfigs.filter((item) => item.id !== deleteId));
    try {
      const res = await deleteSystemConfig(deleteId);
      if (res.status === 200) {
        toast.success("Config deleted successfully");
        fetchConfigData();
      }
    } catch (err) {
      console.error("Failed to delete configuration", err);
      toast.error(err.response?.data?.error || "Failed to delete config");
    }
    setDeleteId(null);
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!validateForm()) return;
    try {
      const payload = {
        name: name.trim(),
        value: value.toString().trim(),
        value_type: dataType,
        comment: description.trim(),
      };
      const res = isEditMode
        ? await updateSystemConfig(currentConfigId, payload)
        : await addSystemConfig(payload);
      if (res?.status === 201 || res?.status == 200) {
        //refresh list
        fetchConfigData();
        //clear data
        setName(null);
        setValue(null);
        setDescription(null);
        selectDataType("string");
        //close modal
        setIsModalOpen(false);
        setIsEditMode(false);
      }
    } catch (err) {
      console.log("failed to add config :", err);
    }
  };

  return (
    <Fragment>
      <div className="relative w-full space-y-4 max-h-fit pb-28">
        <div className="w-full flex flex-row items-center justify-start  space-x-4">
          {/* Search */}
          <div>
            <input
              type="text"
              placeholder="Search by name or value"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1); // reset to first page on search
              }}
              className="w-[300px]  text-xs py-2.5 px-4 bg-slate-400/20 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-800 dark:text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
            />
          </div>
          <SolidButton
            className="px-4 py-2 text-sm space-x-2"
            onClick={() => setIsModalOpen(true)}
          >
            <CirclePlus size={16} onClick={() => {}} />
            <span>Add Config</span>
          </SolidButton>
        </div>
        <MyCard className="relative h-auto">
          <table className="min-w-full border-collapse ">
            <thead>
              <tr className=" px-2 grid grid-cols-16 bg-gray-800/40 w-full flex items-center justify-start text-xs font-light text-slate-500 border-b border-slate-200 dark:border-slate-700">
                <th className="py-2 md:col-span-3 text-start">Name</th>
                <th className="py-2 md:col-span-5 text-start">Value</th>
                <th className="py-2 md:col-span-2 text-start">Type</th>

                <th className="py-2 md:col-span-3 text-start">Comment</th>
                <th className="py-2 md:col-span-2 text-start"></th>
                <th className="py-2 md:col-span-2 text-end">Action</th>
              </tr>
            </thead>
            <tbody>
              {/* data rows */}
              {systemConfigs.map((config, index) => (
                <tr
                  key={index}
                  className="border-b grid grid-cols-16  px-2 items-center justify-start border-slate-200 dark:border-slate-700"
                >
                  {/* Name */}
                  <td className="py-3 md:col-span-3 pl-2">
                    <div className="flex  items-center ">
                      <span className="text-xs  text-start font-medium text-slate-800 dark:text-slate-300 overflow-hidden whitespace-nowrap text-ellipsis">
                        {config.name}
                      </span>
                    </div>
                  </td>
                  {/* Value */}
                  <td className="py-3   md:col-span-5">
                    <div className="flex">
                      <span className="text-xs text-slate-600 dark:text-slate-400 overflow-hidden whitespace-nowrap text-ellipsis text-start">
                        {config.value.toString()}
                      </span>
                    </div>
                  </td>
                  {/* Data Type */}
                  <td className="py-3  md:col-span-2">
                    <span
                      className={`text-xs text-start text-slate-600 dark:text-blue-400 border border-blue-600 bg-blue-600/20 px-2 py-0.5 rounded-full`}
                    >
                      {config.value_type}
                    </span>
                  </td>

                  {/* Comment */}
                  <td className="py-3  md:col-span-4">
                    <div className="flex">
                      <span className="text-xs text-slate-600 dark:text-slate-400  overflow-hidden whitespace-nowrap text-ellipsis">
                        {config.comment || "No comment provided"}
                      </span>
                    </div>
                  </td>
                  <td className="py-3 md:col-span-2 flex items-center  justify-center space-x-4 ">
                    {/* edit icon */}
                    {/* delete icon*/}
                    <PencilSimpleLine
                      size={20}
                      weight="bold"
                      onClick={() => {
                        openEditModal(config);
                      }}
                      className="dark:text-slate-400 text-slate-600 cursor-pointer"
                    />
                    <Trash
                      onClick={() => setDeleteId(config.id)}
                      size={20}
                      className="text-red-500 cursor-pointer"
                    />
                  </td>
                </tr>
              ))}

              {/* more rows can be added here */}
            </tbody>
          </table>
        </MyCard>
        <div className="w-full b-0 absolute">
          <Pagination
            page={page}
            limit={limit}
            total={total}
            fixed={false}
            onPageChange={setPage}
            onLimitChange={(l) => {
              setLimit(l);
              setPage(1); // reset to first page
            }}
            constrainTo="#devices-main"
          />
        </div>
      </div>
      {/* Add and update */}
      <MyModal
        isOpen={isModalOpen}
        title={isEditMode ? "Edit Configuration" : "Add New Configuration"}
        onClose={() => setIsModalOpen(false)}
        width="560px"
      >
        <div className="w-full space-y-4 mt-4">
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">
              Configuration Name*
            </label>
            <input
              type="text"
              placeholder="e.g. API_KEY or DB_PASSWORD"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (errors.name)
                  setErrors((prev) => ({ ...prev, name: undefined }));
              }}
              aria-invalid={!!errors.name}
              className={`w-full text-xs py-2.5 px-4 bg-slate-400/20 dark:bg-slate-800 border rounded-xl text-slate-800 dark:text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${
                errors.name
                  ? "border-red-400"
                  : "border-slate-200 dark:border-slate-700"
              }`}
            />
            <span className="text-[10px] dark:text-slate-500 font-light">
              Uppercase letters,numbers,underscores only (e.g.,API_SECRET)
            </span>
            {errors.name && (
              <div className="text-xs text-red-500 mt-1">{errors.name}</div>
            )}
          </div>
          {/* Type */}
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">
              Type*
            </label>
            <select
              id="dataType"
              name="dataType"
              value={dataType}
              onChange={(e) => {
                selectDataType(e.target.value);
                if (e.target.value === "boolean") {
                  setValue("false");
                }
                if (errors.value)
                  setErrors((prev) => ({ ...prev, value: undefined }));
              }}
              className="w-full text-xs py-2.5 px-4 bg-slate-400/20 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-800 dark:text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
            >
              <option value="string">STRING</option>
              <option value="number">INT</option>
              <option value="boolean">BOOLEAN</option>
            </select>
          </div>
          {/* Data Type */}
          {dataType === "boolean" ? (
            <div className="flex flex-col">
              <label className="block text-xs font-medium text-slate-500 mb-1">
                Configuration Value*
              </label>
              <BigToggle
                size={40}
                onChange={(value) => {
                  setValue(value);
                }}
              />
            </div>
          ) : (
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">
                Configuration Value*
              </label>
              <input
                type={dataType === "number" ? "number" : "text"}
                placeholder="e.g. 12345-ABCDE or true"
                value={value}
                onChange={(e) => {
                  setValue(e.target.value);
                  if (errors.value)
                    setErrors((prev) => ({ ...prev, value: undefined }));
                }}
                aria-invalid={!!errors.value}
                className={`toggle w-full text-xs py-2.5 px-4 bg-slate-400/20 dark:bg-slate-800 border rounded-xl text-slate-800 dark:text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${
                  errors.value
                    ? "border-red-400"
                    : "border-slate-200 dark:border-slate-700"
                }`}
              />
              <span className="text-[10px] dark:text-slate-500 font-light">
                enter text value
              </span>
              {errors.value && (
                <div className="text-xs text-red-500 mt-1">{errors.value}</div>
              )}
            </div>
          )}

          {/* description */}
          <div className="mt-4">
            <label className="block text-xs font-medium text-slate-500 mb-1">
              Description*
            </label>
            <textarea
              type="text"
              placeholder="e.g. This is the API key for accessing the payment gateway. Keep it secret!"
              value={description}
              onChange={(e) => {
                setDescription(e.target.value);
                if (errors.description)
                  setErrors((prev) => ({ ...prev, description: undefined }));
              }}
              aria-invalid={!!errors.description}
              className={`w-full h-20 text-xs py-2.5 px-4 bg-slate-400/20 dark:bg-slate-800 border rounded-xl text-slate-800 dark:text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${
                errors.description
                  ? "border-red-400"
                  : "border-slate-200 dark:border-slate-700"
              }`}
            />
            {errors.description && (
              <div className="text-xs text-red-500 mt-1">
                {errors.description}
              </div>
            )}
          </div>

          <div className="flex space-x-4 justify-end mt-6">
            <BorderButton onClick={() => setIsModalOpen(false)}>
              <span className="text-sm text-slate-400">Cancel</span>
            </BorderButton>
            <SolidButton
              disabled={isDisabled}
              className="px-4 py-2 text-sm space-x-2"
              onClick={(e) => handleSubmit(e)}
            >
              {isEditMode ? "Update Configuration" : "Create Configuration"}
            </SolidButton>
          </div>
        </div>
      </MyModal>
      {/* DELETE CONFIRM MODAL */}
      <MyModal
        isOpen={!!deleteId}
        title="Delete Configuration?"
        onClose={() => setDeleteId(null)}
      >
        <p className="text-sm text-slate-800 dark:text-slate-300 mt-4">
          Are you sure you want to delete this config?
        </p>

        <div className="flex justify-end space-x-3 mt-6">
          <button
            onClick={() => setDeleteId(null)}
            className="px-4 py-2 rounded-lg bg-slate-400/20 dark:bg-slate-800 text-sm"
          >
            Cancel
          </button>

          <SolidButton onClick={handleDelete} children={<span>Delete</span>} />
        </div>
      </MyModal>
    </Fragment>
  );
}
